import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Calendar, Clock, MapPin, User as UserIcon, Briefcase,
  Sparkles, Headphones, Download, Loader, Trash2, Edit3, CheckCircle2,
  Volume2, Pause, Play, Linkedin, AlertCircle, RotateCcw,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * InterviewDetail — single interview prep page.
 *
 * Top: countdown clock + role/company headline + .ics download
 * Middle: bundled briefing (text from LLM, audio from ElevenLabs)
 * Bottom: tied tools (Strategy Brief / Playbook / Interviewer Research) + notes
 */
export default function InterviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [generatingBriefing, setGeneratingBriefing] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioObjectUrl, setAudioObjectUrl] = useState("");
  const audioRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/interviews/${id}`);
      setInterview(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load interview.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // Calendar deep-link → briefing. When opened from a calendar event whose
  // URL was set to /interviews/<id>#briefing (the .ics URL property), scroll
  // the briefing card into view once the interview data lands. Runs once per
  // load — the hash isn't cleared so a manual re-trigger needs a re-nav.
  useEffect(() => {
    if (!interview) return;
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#briefing") return;
    // Defer past paint so the card is in the DOM.
    const t = setTimeout(() => {
      const el = document.getElementById("briefing");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, [interview]);

  // Live countdown every second
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const countdown = useMemo(() => {
    if (!interview) return null;
    const target = new Date(interview.interview_at).getTime();
    const diffMs = target - now;
    if (diffMs <= 0) return { passed: true, label: "Interview time" };
    const days = Math.floor(diffMs / 86400000);
    const hours = Math.floor((diffMs % 86400000) / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    let label = "";
    if (days > 0) label = `${days}d ${hours}h ${minutes}m`;
    else if (hours > 0) label = `${hours}h ${minutes}m ${seconds}s`;
    else label = `${minutes}m ${seconds}s`;
    return { passed: false, label, days, hours, minutes, urgent: diffMs < 3600000 };
  }, [now, interview]);

  const generateBriefing = async () => {
    setGeneratingBriefing(true);
    setError("");
    try {
      const { data } = await api.post(`/interviews/${id}/briefing`);
      setInterview((prev) => ({ ...prev, briefing_text: data.briefing_text, briefing_audio: null }));
      // Invalidate cached audio
      if (audioObjectUrl) {
        URL.revokeObjectURL(audioObjectUrl);
        setAudioObjectUrl("");
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't generate briefing.");
    } finally {
      setGeneratingBriefing(false);
    }
  };

  const generateAudio = async () => {
    setGeneratingAudio(true);
    setError("");
    try {
      await api.post(`/interviews/${id}/audio`);
      // Fetch the audio blob
      const audioResp = await api.get(`/interviews/${id}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(audioResp.data);
      if (audioObjectUrl) URL.revokeObjectURL(audioObjectUrl);
      setAudioObjectUrl(url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't generate audio.");
    } finally {
      setGeneratingAudio(false);
    }
  };

  // Auto-fetch existing audio when present. Defensive: only mount an
  // ObjectURL if the response is actually a non-empty audio blob —
  // otherwise the <audio> element fires a media error that the CRA dev
  // overlay catches as a generic "Script error" with no actionable details.
  useEffect(() => {
    let cancelled = false;
    const fetchAudio = async () => {
      if (!interview?.briefing_text) return;
      try {
        const audioResp = await api.get(`/interviews/${id}/audio`, { responseType: "blob" });
        if (cancelled) return;
        const blob = audioResp?.data;
        if (
          !blob ||
          typeof blob.size !== "number" ||
          blob.size < 64 ||
          (blob.type && !blob.type.startsWith("audio/"))
        ) {
          // Looks like an error blob (JSON 404, empty, or wrong mime). Skip
          // mounting it on <audio> so the overlay doesn't catch a media error.
          return;
        }
        const url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setAudioObjectUrl(url);
      } catch (_e) {
        // 404 == audio not generated yet, which is fine
      }
    };
    fetchAudio();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, interview?.briefing_text]);

  // Stop the <audio> element from leaking media errors into CRA's
  // react-error-overlay window.onerror handler (it surfaces them as the
  // useless "Script error." red banner). We log to console for diagnostics
  // but otherwise swallow.
  const handleAudioError = (e) => {
    try {
      e?.preventDefault?.();
      e?.stopPropagation?.();
    } catch { /* noop */ }
    // eslint-disable-next-line no-console
    console.warn("[interview-audio] media load failed; clearing ObjectURL");
    if (audioObjectUrl) {
      try { URL.revokeObjectURL(audioObjectUrl); } catch { /* noop */ }
      setAudioObjectUrl("");
    }
    setAudioPlaying(false);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (audioPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  const downloadIcs = () => {
    if (!interview?.ics_url) return;
    // ics_url is relative — prepend the api origin
    const origin = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    window.open(`${origin}${interview.ics_url}`, "_blank");
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this scheduled interview?")) return;
    try {
      await api.delete(`/interviews/${id}`);
      navigate("/interviews");
    } catch (_e) {
      setError("Couldn't delete.");
    }
  };

  const markComplete = async () => {
    try {
      const { data } = await api.patch(`/interviews/${id}`, { status: "completed" });
      setInterview((prev) => ({ ...prev, ...data }));
    } catch (_e) {
      setError("Couldn't mark complete.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
          <div className="rm-card-static p-6 flex items-center gap-2">
            <Loader size={16} className="animate-spin" style={{ color: "#FF6A00" }} />
            <span className="font-mono-rm text-[11px] uppercase tracking-widest">Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  if (!interview) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
          <div className="rm-card-static p-6">
            <h2 className="font-display text-xl uppercase mb-2">Not found</h2>
            <p className="text-sm">{error || "This interview doesn't exist or has been removed."}</p>
            <button onClick={() => navigate("/interviews")} className="rm-btn rm-btn-primary mt-4 text-xs">
              <ArrowLeft size={12} /> Back to interviews
            </button>
          </div>
        </div>
      </div>
    );
  }

  const interviewAt = new Date(interview.interview_at);

  return (
    <div className="min-h-screen rm-paper" data-testid="interview-detail-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-10">
        {/* Back */}
        <button
          type="button"
          onClick={() => navigate("/interviews")}
          className="font-mono-rm uppercase text-[10px] tracking-widest mb-3 inline-flex items-center gap-1"
          style={{ color: "#475569", fontWeight: 700 }}
          data-testid="interview-back-btn"
        >
          <ArrowLeft size={11} /> All interviews
        </button>

        {/* Countdown + headline */}
        <div
          className="rm-card-static p-5 md:p-7 mb-5"
          style={{
            background: countdown?.urgent
              ? "linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%)"
              : "linear-gradient(135deg, #0F1B2D 0%, #1E3A5F 100%)",
            color: "white",
            borderColor: "#0F1B2D",
            boxShadow: "5px 5px 0 0 #FF6A00",
          }}
          data-testid="interview-countdown-card"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1"
                   style={{ color: countdown?.urgent ? "#FECACA" : "#FFC145", fontWeight: 700 }}>
                · {interview.status === "completed" ? "Completed interview" : countdown?.passed ? "Interview time" : "Time until interview"}
              </div>
              <div
                className="font-display uppercase leading-none mb-2"
                style={{
                  fontSize: "clamp(2rem, 5vw, 3.5rem)",
                  color: "white",
                  letterSpacing: "-0.03em",
                }}
                data-testid="interview-countdown-label"
              >
                {countdown?.passed ? "It's time" : countdown?.label || "—"}
              </div>
              <div className="font-display uppercase leading-tight"
                   style={{ fontSize: "1.1rem", color: "white", letterSpacing: "-0.01em" }}>
                {interview.role_title}
              </div>
              <div className="font-mono-rm text-[11px] uppercase tracking-widest mt-1"
                   style={{ color: "#CBD5E1" }}>
                <Briefcase size={11} style={{ display: "inline", marginRight: 4 }} />
                {interview.role_company}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest"
                   style={{ color: "#FFC145" }}>
                · {interviewAt.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </div>
              <button
                type="button"
                onClick={downloadIcs}
                className="rm-btn text-[10px] px-2 py-1.5"
                style={{ background: "#FFC145", color: "#0F1B2D", border: "2px solid #FFC145", fontWeight: 700 }}
                data-testid="interview-ics-btn"
                title="Download .ics for Google/Apple Calendar"
              >
                <Download size={11} /> Add to Calendar (.ics)
              </button>
            </div>
          </div>

          {/* Meta strip */}
          <div className="mt-3 flex flex-wrap gap-3 text-[11.5px]" style={{ color: "#E2E8F0" }}>
            {interview.interviewer_name && (
              <span className="inline-flex items-center gap-1">
                <UserIcon size={11} /> With {interview.interviewer_name}
              </span>
            )}
            {interview.interviewer_linkedin_url && (
              <a href={interview.interviewer_linkedin_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-2"
                 style={{ color: "#A7F3D0" }}>
                <Linkedin size={11} /> LinkedIn
              </a>
            )}
            {interview.location && (
              <span className="inline-flex items-center gap-1 truncate" style={{ maxWidth: 240 }}>
                <MapPin size={11} /> {interview.location}
              </span>
            )}
          </div>

          {/* Reminders strip */}
          <ReminderStrip reminders={interview.reminders} />
        </div>

        {error && (
          <div className="rm-card-static p-3 mb-4 text-xs font-mono-rm"
               style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}
               data-testid="interview-error">
            · {error}
          </div>
        )}

        {/* Briefing section */}
        <div
          id="briefing"
          className="rm-card-static p-5 md:p-6 mb-5"
          style={{ background: "white", borderColor: "#0F1B2D", boxShadow: "4px 4px 0 0 #047857" }}
          data-testid="interview-briefing-card"
        >
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Sparkles size={16} style={{ color: "#047857" }} />
            <h2 className="font-display uppercase text-lg leading-tight"
                style={{ color: "#0F1B2D", letterSpacing: "-0.01em" }}>
              Your 5-minute briefing
            </h2>
            {interview.briefing_text && (
              <button
                type="button"
                onClick={generateBriefing}
                disabled={generatingBriefing}
                className="rm-btn rm-btn-ghost text-[10px] px-2 py-1 ml-auto"
                data-testid="interview-regen-briefing-btn"
                title="Regenerate the briefing"
              >
                {generatingBriefing ? <Loader size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                Regenerate
              </button>
            )}
          </div>

          {!interview.briefing_text ? (
            <div>
              <p className="text-sm leading-relaxed mb-3" style={{ color: "#475569" }}>
                Resmo will bundle anything tied to this interview — your tailored
                resume, Strategy Brief, Interviewer Research, and Playbook — into
                a calm, spoken-style briefing you can read or listen to.
              </p>
              <button
                type="button"
                onClick={generateBriefing}
                disabled={generatingBriefing}
                className="rm-btn rm-btn-primary text-sm px-4 py-2.5"
                data-testid="interview-generate-briefing-btn"
              >
                {generatingBriefing ? <Loader size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {generatingBriefing ? "Generating… (~30s)" : "Generate briefing"}
              </button>
              <p className="font-mono-rm text-[10px] uppercase tracking-widest mt-2"
                 style={{ color: "#94A3B8" }}>
                · 5 credits · Pro free
              </p>
            </div>
          ) : (
            <>
              {/* Audio player strip */}
              <div
                className="rm-card-static p-3 mb-3"
                style={{ background: "#ECFDF5", borderColor: "#047857" }}
                data-testid="interview-audio-strip"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <Headphones size={18} style={{ color: "#047857" }} />
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-display uppercase text-sm leading-tight"
                         style={{ color: "#064E3B", letterSpacing: "-0.01em" }}>
                      Listen on the way in
                    </div>
                    <div className="font-mono-rm text-[10px] uppercase tracking-widest"
                         style={{ color: "#047857" }}>
                      · Narrated by Resmo's calm voice
                    </div>
                  </div>
                  {audioObjectUrl ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={togglePlay}
                        className="rm-btn text-xs px-3 py-2"
                        style={{ background: "#047857", color: "white", border: "2px solid #064E3B", fontWeight: 700 }}
                        data-testid="interview-audio-play-btn"
                      >
                        {audioPlaying ? <Pause size={12} /> : <Play size={12} fill="white" />}
                        {audioPlaying ? "Pause" : "Play"}
                      </button>
                      <a
                        href={audioObjectUrl}
                        download={`resmo-briefing-${id.slice(0, 8)}.mp3`}
                        className="rm-btn rm-btn-ghost text-[10px] px-2 py-1.5"
                        data-testid="interview-audio-download-btn"
                      >
                        <Download size={11} /> MP3
                      </a>
                      <audio
                        ref={audioRef}
                        src={audioObjectUrl}
                        onPlay={() => setAudioPlaying(true)}
                        onPause={() => setAudioPlaying(false)}
                        onEnded={() => setAudioPlaying(false)}
                        onError={handleAudioError}
                        controls
                        preload="none"
                        style={{ height: 32 }}
                        data-testid="interview-audio-element"
                      />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={generateAudio}
                      disabled={generatingAudio}
                      className="rm-btn text-xs px-3 py-2"
                      style={{ background: "#047857", color: "white", border: "2px solid #064E3B", fontWeight: 700 }}
                      data-testid="interview-generate-audio-btn"
                    >
                      {generatingAudio ? <Loader size={12} className="animate-spin" /> : <Volume2 size={12} />}
                      {generatingAudio ? "Generating… (~40s)" : "Generate audio"}
                    </button>
                  )}
                </div>
              </div>

              {/* Briefing text */}
              <div
                className="rm-card-static p-4"
                style={{ background: "#FFFBEB", borderColor: "#FFC145" }}
                data-testid="interview-briefing-text"
              >
                <pre
                  className="whitespace-pre-wrap font-serif text-[14.5px] leading-relaxed m-0"
                  style={{ color: "#0F1B2D", fontFamily: "'Georgia', 'Times New Roman', serif" }}
                >
                  {interview.briefing_text}
                </pre>
              </div>
            </>
          )}
        </div>

        {/* Notes */}
        {interview.notes && (
          <div className="rm-card-static p-4 mb-5"
               style={{ background: "#FEF3C7", borderColor: "#CA8A04" }}
               data-testid="interview-notes-card">
            <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1"
                 style={{ color: "#78350F", fontWeight: 700 }}>
              · Your notes
            </div>
            <p className="text-sm leading-snug" style={{ color: "#78350F" }}>
              {interview.notes}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2" data-testid="interview-actions">
          {(() => {
            const interviewPastBy24h = (Date.now() - new Date(interview.interview_at).getTime()) > 0;
            const debriefDone = !!interview.debrief_submitted_at;
            if (interviewPastBy24h && !debriefDone) {
              return (
                <button
                  type="button"
                  onClick={() => navigate(`/interviews/${id}/debrief`)}
                  className="rm-btn text-xs px-3 py-2"
                  style={{ background: "#047857", color: "white", border: "2px solid #064E3B", boxShadow: "3px 3px 0 0 #064E3B", fontWeight: 700 }}
                  data-testid="interview-debrief-btn"
                >
                  <Sparkles size={12} /> Log a 30-second debrief
                </button>
              );
            }
            if (debriefDone) {
              return (
                <span
                  className="rm-tag inline-flex items-center"
                  style={{ background: "#D1FAE5", color: "#047857", border: "2px solid #047857" }}
                  data-testid="interview-debriefed-pill"
                >
                  <CheckCircle2 size={12} /> Debriefed
                </span>
              );
            }
            return null;
          })()}
          {interview.status === "scheduled" && (
            <button
              type="button"
              onClick={markComplete}
              className="rm-btn rm-btn-ghost text-xs px-3 py-2"
              data-testid="interview-mark-complete-btn"
            >
              <CheckCircle2 size={12} /> Mark complete
            </button>
          )}
          <button
            type="button"
            onClick={handleDelete}
            className="rm-btn rm-btn-ghost text-xs px-3 py-2"
            style={{ color: "#B91C1C", borderColor: "#B91C1C" }}
            data-testid="interview-delete-btn"
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function ReminderStrip({ reminders = {} }) {
  const labels = [
    { key: "7d", label: "7d" },
    { key: "24h", label: "24h" },
    { key: "1h", label: "1h" },
  ];
  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="interview-reminders-strip">
      <span className="font-mono-rm text-[10px] uppercase tracking-widest"
            style={{ color: "#94A3B8" }}>
        Reminders:
      </span>
      {labels.map(({ key, label }) => {
        const sent = reminders?.[key]?.sent_at;
        return (
          <span
            key={key}
            className="font-mono-rm text-[10px] uppercase tracking-widest inline-flex items-center gap-1 px-2 py-0.5 rounded"
            style={{
              background: sent ? "#047857" : "rgba(255,255,255,0.12)",
              color: sent ? "white" : "#FFC145",
              border: sent ? "1px solid #064E3B" : "1px solid #475569",
            }}
            data-testid={`interview-reminder-${key}`}
          >
            {sent && <CheckCircle2 size={10} />}
            {label}
          </span>
        );
      })}
    </div>
  );
}
